import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { formatDate } from "../utils/patientUtils";

const formatTreatmentEntryLabel = (entry: any) =>
  [entry?.treatment, entry?.type, entry?.subtype, entry?.stage]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" · ");

const formatPrevTreatmentStatus = (status?: string) => {
  if (!status) return "Ongoing";
  if (status === "long term") return "Treatment Plan";
  if (status === "planned") return "Planned";
  if (status === "ongoing") return "Ongoing";
  if (status === "Complete") return "Complete";
  return status;
};

const getPrevTreatmentStatusClass = (status?: string) => {
  if (status === "Complete") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  }
  if (status === "planned" || status === "long term") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  }
  return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300";
};

export const sortTreatmentsByUpdatedDesc = (treatments: any[]) =>
  [...treatments].sort((a: any, b: any) => {
    const aTime = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bTime = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return bTime - aTime;
  });

type ExistingTreatmentConfirmModalProps = {
  open: boolean;
  treatments: any[];
  onCancel: () => void;
  onCreateNew: () => void;
  onOpenTreatment: (treatmentId: string) => void;
};

const ExistingTreatmentConfirmModal: React.FC<
  ExistingTreatmentConfirmModalProps
> = ({ open, treatments, onCancel, onCreateNew, onOpenTreatment }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="treatment-confirm-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div>
            <h2
              id="treatment-confirm-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              Patient already has treatment
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Open an existing treatment below, or create a new one.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="px-5 pb-3">
          <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700">
            {treatments.map((item: any, index: number) => {
              const treatmentItems = Array.isArray(item?.treatments)
                ? item.treatments
                : [];
              const labels = treatmentItems
                .map((t: any) => formatTreatmentEntryLabel(t))
                .filter(Boolean);
              const diagnoseCount = Array.isArray(item?.diagnose)
                ? item.diagnose.length
                : 0;
              const status = item?.status || "Ongoing";

              return (
                <button
                  type="button"
                  key={item?._id || index}
                  onClick={() => onOpenTreatment(item?._id)}
                  className="w-full text-left px-3 py-2.5 bg-slate-50/80 dark:bg-slate-900/40 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      {formatDate(item?.updatedAt)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${getPrevTreatmentStatusClass(
                          status
                        )}`}
                      >
                        {formatPrevTreatmentStatus(status)}
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-indigo-600 dark:text-indigo-300">
                        <RemoveRedEyeIcon sx={{ fontSize: 14 }} />
                        Open
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-800 dark:text-slate-200 line-clamp-2">
                    {labels.length > 0
                      ? labels.join(", ")
                      : "No treatment items yet"}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {treatmentItems.length} treatment
                    {treatmentItems.length === 1 ? "" : "s"}
                    {diagnoseCount > 0
                      ? ` · ${diagnoseCount} diagnose${
                          diagnoseCount === 1 ? "" : "s"
                        }`
                      : ""}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 pb-5 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-500 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onCreateNew}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            Create New
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExistingTreatmentConfirmModal;
